import { useEffect, useState } from "react";
import { useSession } from "../auth/useSession";
import { scopedBrowserStorageKey } from "./scopedBrowserStorage";

/**
 * „Zuletzt angesehen“ auf der Startseite (#631): die zuletzt geöffneten Artikel
 * und Belege, je Person im Browser gemerkt. Nur eine Abkürzung – fehlt der
 * Speicher, bleibt die Liste leer und nichts sonst ändert sich.
 */
export type RecentlyViewedKind = "article" | "beleg";
export type RecentlyViewedEntry = {
  kind: RecentlyViewedKind;
  key: string;
  label: string;
  detail: string | null;
  viewed_at: string;
};

const NAMESPACE = "truucost.recently-viewed.v1";
const MAX_ENTRIES = 8;
const CHANGE_EVENT = "truucost:recently-viewed";

export function recentlyViewedHref(entry: Pick<RecentlyViewedEntry, "kind" | "key">): string {
  const key = encodeURIComponent(entry.key);
  return entry.kind === "article" ? `/articles/${key}` : `/belege/${key}`;
}

/** Neuester Eintrag zuerst; derselbe Artikel oder Beleg steht nur einmal darin. */
export function addRecentlyViewed(
  entries: RecentlyViewedEntry[],
  entry: RecentlyViewedEntry,
): RecentlyViewedEntry[] {
  const rest = entries.filter((item) => item.kind !== entry.kind || item.key !== entry.key);
  return [entry, ...rest].slice(0, MAX_ENTRIES);
}

function isEntry(value: unknown): value is RecentlyViewedEntry {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return (
    (item.kind === "article" || item.kind === "beleg") &&
    typeof item.key === "string" && item.key !== "" &&
    typeof item.label === "string" &&
    (item.detail === null || typeof item.detail === "string") &&
    typeof item.viewed_at === "string"
  );
}

function read(key: string | null): RecentlyViewedEntry[] {
  if (!key) return [];
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(value) ? value.filter(isEntry).slice(0, MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}

function useStorageKey(): string | null {
  const { user } = useSession();
  return scopedBrowserStorageKey(NAMESPACE, user);
}

/** Merkt sich die geöffnete Seite, sobald ihr Titel bekannt ist. */
export function useRecordRecentlyViewed(
  entry: Omit<RecentlyViewedEntry, "viewed_at"> | null,
): void {
  const storageKey = useStorageKey();
  const kind = entry?.kind;
  const key = entry?.key;
  const label = entry?.label;
  const detail = entry?.detail ?? null;
  useEffect(() => {
    if (!storageKey || !kind || !key || !label) return;
    const next = addRecentlyViewed(read(storageKey), {
      kind,
      key,
      label,
      detail,
      viewed_at: new Date().toISOString(),
    });
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      /* Ohne Speicher gibt es keine Liste; die Seite selbst bleibt unberührt. */
    }
  }, [storageKey, kind, key, label, detail]);
}

export function useRecentlyViewed(): RecentlyViewedEntry[] {
  const storageKey = useStorageKey();
  const [state, setState] = useState(() => ({ storageKey, entries: read(storageKey) }));
  useEffect(() => {
    const refresh = () => setState({ storageKey, entries: read(storageKey) });
    refresh();
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [storageKey]);
  return state.storageKey === storageKey ? state.entries : read(storageKey);
}
